/**
 * Gemini Live turn detection configuration.
 *
 * Mirrors Google's Gemini Live API surface 1:1 — every value here is a value
 * the API accepts. No invented enum values, no eagerness abstraction.
 *
 * Single mode for v1: automatic activity detection. Manual mode (caller emits
 * activityStart/activityEnd events) is not exposed — adds protocol complexity
 * without a real customer ask. Add it when needed.
 *
 * @see https://ai.google.dev/api/live#AutomaticActivityDetection
 */

import { z } from "zod";

// ============================================================
// Enums (verbatim API values)
// ============================================================

/**
 * Valid `start_of_speech_sensitivity` values per Google's Gemini Live API.
 *
 * The API enum has three values total: UNSPECIFIED (default = HIGH), HIGH, LOW.
 * There is NO "medium" — that mistake cost a production outage on 2026-04-24.
 */
export const GEMINI_START_SENSITIVITY = ["unspecified", "high", "low"] as const;
export type GeminiStartSensitivity = (typeof GEMINI_START_SENSITIVITY)[number];

/** Valid `end_of_speech_sensitivity` values per Google's Gemini Live API. */
export const GEMINI_END_SENSITIVITY = ["unspecified", "high", "low"] as const;
export type GeminiEndSensitivity = (typeof GEMINI_END_SENSITIVITY)[number];

// ============================================================
// Schema
// ============================================================

/**
 * Gemini Live automatic activity detection.
 *
 * The knobs map directly to `realtimeInputConfig.automaticActivityDetection`
 * in the WebSocket setup payload — no translation, no provider abstraction.
 * The UI exposes the two sensitivity dropdowns directly so users see exactly
 * what the API accepts.
 *
 * `interruptResponse` is the deployment's choice for
 * `realtimeInputConfig.activityHandling`: `true` is the API default,
 * `START_OF_ACTIVITY_INTERRUPTS` (the caller's speech interrupts the model —
 * "barge in"); `false` is `NO_INTERRUPTION`.
 *
 * @see https://ai.google.dev/api/live — `ActivityHandling`
 */
export const GeminiLiveTurnDetectionSchema = z.object({
  /** START sensitivity. HIGH = detect start more often (default). LOW = detect less often. */
  startSensitivity: z.enum(GEMINI_START_SENSITIVITY),
  /** END sensitivity. HIGH = end speech more often (default). LOW = end less often. */
  endSensitivity: z.enum(GEMINI_END_SENSITIVITY),
  /**
   * Detected speech required before start-of-speech is committed (ms). Lower
   * catches shorter speech and raises false starts. Absent = Google's default,
   * which it does not publish.
   */
  prefixPaddingMs: z.number().min(0).max(2000).optional(),
  /**
   * Non-speech required before end-of-speech is committed (ms). Longer lets the
   * caller pause without losing the turn, and adds that much latency to every
   * reply. Absent = Google's default, which it does not publish.
   */
  silenceDurationMs: z.number().min(100).max(5000).optional(),
  /** Whether the caller's speech interrupts the model — `activityHandling`, see above. */
  interruptResponse: z.boolean()
});

export type GeminiLiveTurnDetection = z.infer<typeof GeminiLiveTurnDetectionSchema>;

// ============================================================
// Defaults
// ============================================================

/**
 * Default turn detection for new Gemini Live voice deployments.
 *
 * Both sensitivities at HIGH = Google's documented default = balanced behavior
 * (detects speech onset eagerly, ends turn eagerly).
 */
export const DEFAULT_GEMINI_LIVE_TURN_DETECTION: GeminiLiveTurnDetection = {
  startSensitivity: "high",
  endSensitivity: "high",
  interruptResponse: true
};
